<script setup>
import { ref, onBeforeMount, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import api from "@/api/axios";

const body = document.body;
const store = useStore();
const route = useRoute();
const router = useRouter();

const token = ref(route.query.token || "");
const password = ref("");
const confirmPassword = ref("");
const error = ref("");
const success = ref(false);
const loading = ref(false);

onBeforeMount(() => {
  store.state.hideConfigButton = true;
  store.state.showNavbar = false;
  store.state.showSidenav = false;
  store.state.showFooter = false;
  body.classList.remove("bg-gray-100");
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  store.state.showNavbar = true;
  store.state.showSidenav = true;
  store.state.showFooter = true;
  body.classList.add("bg-gray-100");
});

const handleSubmit = async () => {
  if (loading.value) return;

  if (!password.value || !confirmPassword.value) {
    error.value = "Debes completar todos los campos";
    return;
  }

  if (password.value.length < 8) {
    error.value = "La contraseña debe tener mínimo 8 caracteres";
    return;
  }

  if (password.value !== confirmPassword.value) {
    error.value = "Las contraseñas no coinciden";
    return;
  }

  loading.value = true;
  error.value = "";

  try {
    await api.post("/reset-password", {
      token: token.value,
      newPassword: password.value,
    });

    success.value = true;

    setTimeout(() => {
      router.push("/signin");
    }, 3000);
  } catch (err) {
    error.value =
      err.response?.data?.error || "No se pudo restablecer la contraseña";
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <main class="mt-0 main-content">
    <section>
      <div class="page-header min-vh-100">
        <div class="container">
          <div class="row justify-content-center">
            <div class="mx-auto col-xl-4 col-lg-5 col-md-7 d-flex flex-column">
              <div class="card card-plain mt-8">
                <div class="pb-0 card-header text-start">
                  <h4 class="font-weight-bolder">Restablecer contraseña</h4>
                  <p class="mb-0">
                    Crea una nueva contraseña para tu cuenta.
                  </p>
                </div>

                <div class="card-body">
                  <!-- Sin token en la URL -->
                  <div v-if="!token" class="text-center">
                    <p class="text-danger">
                      El enlace es inválido. Solicita uno nuevo desde
                      "¿Olvidaste tu contraseña?".
                    </p>
                    <router-link
                      to="/forgot-password"
                      class="text-success text-gradient font-weight-bold"
                    >
                      Solicitar nuevo enlace
                    </router-link>
                  </div>

                  <!-- Éxito -->
                  <div v-else-if="success" class="text-center">
                    <p class="text-success">
                      ✅ Tu contraseña fue restablecida correctamente.
                      Redirigiendo al inicio de sesión...
                    </p>
                    <router-link
                      to="/signin"
                      class="text-success text-gradient font-weight-bold"
                    >
                      Ir a iniciar sesión
                    </router-link>
                  </div>

                  <!-- Formulario -->
                  <form v-else @submit.prevent="handleSubmit">
                    <div class="mb-3">
                      <argon-input
                        v-model="password"
                        type="password"
                        placeholder="Nueva contraseña"
                        size="lg"
                      />
                    </div>

                    <div class="mb-3">
                      <argon-input
                        v-model="confirmPassword"
                        type="password"
                        placeholder="Confirmar contraseña"
                        size="lg"
                      />
                    </div>

                    <div class="text-center">
                      <argon-button
                        class="mt-2"
                        variant="gradient"
                        color="success"
                        fullWidth
                        size="lg"
                        type="submit"
                        :disabled="loading"
                      >
                        {{ loading ? "Guardando..." : "Restablecer contraseña" }}
                      </argon-button>
                    </div>

                    <p v-if="error" class="text-danger mt-3 text-center">
                      {{ error }}
                    </p>
                  </form>
                </div>

                <div
                  v-if="token && !success"
                  class="px-1 pt-0 text-center card-footer px-lg-2"
                >
                  <p class="mx-auto mb-4 text-sm">
                    <router-link
                      to="/signin"
                      class="text-success text-gradient font-weight-bold"
                    >
                      Volver a iniciar sesión
                    </router-link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>
